/**
 * Self-hosted telemetry wrapper for the diary and dashboard apps.
 *
 * Sends fire-and-forget OTLP/HTTP metric increments to the
 * Emper-Tools-operated OTel Collector. Disabled when
 * VUE_APP_METRICS_URL is empty. See Docs/SelfHostedTelemetryPlan.md.
 *
 * Privacy rules (mirror the server-side whitelist):
 *  - No user identifier, with one deliberate exception: a failed
 *    login sends the account it was attempted with, as
 *    `account_raw`. The collector replaces it with a peppered
 *    digest before anything is stored, see trackLoginFailure().
 *  - Closed-set whitelist of payload keys.
 *  - Each value capped at 64 chars (defence in depth).
 *  - fetch() with keepalive; never awaited, never throws.
 *
 * Wire shape:
 *  - Non-timing events are sent as `emper_events_total` counter
 *    increments (DELTA, monotonic).
 *  - Timing events are sent as OTel Histogram metrics:
 *      emper_query_duration_seconds (Perf.Query)
 *      emper_cloud_duration_seconds (Perf.CloudRun)
 *      emper_mutation_duration_seconds (direct Parse.Object writes)
 *      emper_route_duration_seconds (Perf.Route)
 *  - A timing that failed additionally carries `severity`
 *    (warn/error) and `query_method`. A timing that succeeded carries
 *    neither, so the cardinality of the common case is unchanged.
 *    Deliberately no `route_name` here: a histogram costs nine
 *    Prometheus series per attribute combination, so multiplying by
 *    the route count is expensive. If the class and the method ever
 *    turn out not to localise a failure, put the route on the
 *    `emper_events_total` counter, which costs one series instead.
 */

const ALLOWED = new Set([
	'app', 'customer', 'app_version', 'route_name', 'entity_type',
	'query_class', 'function_name', 'event_type',
	'error_class', 'result_count_bucket', 'filter_mode',
	'account_raw', 'severity', 'query_method'
])

// Bucket boundaries in seconds. Matches the human-friendly
// thresholds used by phase-1 string buckets.
const BOUNDS_SECONDS = [0.05, 0.1, 0.3, 1, 3, 10]

let endpoint = undefined
let apiKey   = undefined
let baseline = {}
let optedOut = false

export function initTelemetry(opts) {
	if (!opts || !opts.endpoint) return // empty = disabled
	endpoint = opts.endpoint
	apiKey   = opts.apiKey
	baseline = {
		app:         opts.app,
		customer:    opts.customer,
		app_version: opts.appVersion
	}
}

export function setOptedOut(v) { optedOut = !!v }

/**
 * Marks a query, or an object being deleted, whose row may
 * legitimately be gone -- because another user deleted it, because we
 * are deliberately probing for existence, or because the same
 * best-effort cleanup can run twice. A 101 from a marked query or
 * destroy is reported as a warning rather than an error; an unmarked
 * one stays an error, so forgetting to mark fails loudly rather than
 * silently hiding a real bug.
 *
 * The mark rides on the query or object rather than on the call,
 * which matters: the SDK implements get() as equalTo('objectId') plus
 * first() on the same query, so both the inner and the outer data
 * point see the same flag and agree on the severity.
 *
 * Returns its argument, so it can wrap the constructor or the call:
 *   const query = expectMissing(new Parse.Query('Client'))
 *   expectMissing(notification).destroy()
 */
export function expectMissing(query) {
	if (query) { query.__emperExpectMissing = true }
	return query
}

export function track(type, payload) {
	if (!endpoint || optedOut) return
	const now = nowNanos()
	const merged = Object.assign({}, baseline, whitelist(payload || {}))
	const attrs = [{ key: 'type', value: { stringValue: type } }]
	for (const k of Object.keys(merged)) {
		attrs.push({ key: k, value: { stringValue: String(merged[k]) } })
	}
	postMetrics({
		name: 'emper_events_total',
		sum: {
			dataPoints: [{
				asInt:             '1',
				attributes:        attrs,
				startTimeUnixNano: now,
				timeUnixNano:      now
			}],
			aggregationTemporality: 1, // DELTA
			isMonotonic:            true
		}
	})
}

/**
 * A login that was rejected, keyed on the account attempted, so that
 * one user failing ten times is distinguishable from ten users
 * failing once.
 *
 * The account goes out as `account_raw` and is replaced by a peppered
 * SHA-256 digest inside our own OTel collector before it reaches any
 * storage, see the transform/whitelist block in otel-config.yml.
 * Hashing it here instead would be theatre: everything shipped to a
 * browser is public, so the pepper would be public with it, and
 * logins are e-mail addresses, a small enough domain to reverse an
 * unpeppered digest by dictionary in seconds. It travels in the clear
 * only over TLS, to a host that already receives the same address in
 * every login request.
 *
 * Only failures are keyed. Successful logins and logouts are counted
 * server-side from the Parse container logs, which carry no
 * identifier at all, and note that this signal sees only failures
 * made THROUGH the app. Anything hitting /parse/login directly never
 * runs this code, which is why the alerting is driven by the
 * server-side counter and this feeds a panel only.
 */
export function trackLoginFailure(account, error) {
	track('Auth.Login.Failed', {
		account_raw: account,
		error_class: classifyError(error)
	})
}

/**
 * A login refused before any password was checked. The dashboard is
 * admin-only and asks `isAdmin` first, so a non-admin never reaches
 * Parse.User.logIn, there is no credential failure to log, and
 * without this the attempt leaves no trace anywhere.
 *
 * Keyed on the account for a sharper reason than trackLoginFailure():
 * an admin mistyping their password and someone probing the admin app
 * with an account that has no business there look identical as a bare
 * count, and only the digest separates them. Hashing happens in the
 * collector, see trackLoginFailure() for why it cannot happen here.
 *
 * `reason` must stay a closed set; it becomes a metric label.
 */
export function trackLoginDenied(account, reason) {
	track('Auth.Login.Denied', {
		account_raw: account,
		error_class: reason
	})
}

function trackDuration(metricName, seconds, payload) {
	if (!endpoint || optedOut) return
	const now = nowNanos()
	const merged = Object.assign({}, baseline, whitelist(payload || {}))
	const attrs = []
	for (const k of Object.keys(merged)) {
		attrs.push({ key: k, value: { stringValue: String(merged[k]) } })
	}
	const bucketCounts = new Array(BOUNDS_SECONDS.length + 1).fill('0')
	let idx = BOUNDS_SECONDS.length
	for (let i = 0; i < BOUNDS_SECONDS.length; i++) {
		if (seconds <= BOUNDS_SECONDS[i]) { idx = i; break }
	}
	bucketCounts[idx] = '1'
	postMetrics({
		name: metricName,
		unit: 's',
		histogram: {
			dataPoints: [{
				attributes:        attrs,
				startTimeUnixNano: now,
				timeUnixNano:      now,
				count:             '1',
				sum:               seconds,
				bucketCounts:      bucketCounts,
				explicitBounds:    BOUNDS_SECONDS.slice()
			}],
			aggregationTemporality: 1 // DELTA
		}
	})
}

function postMetrics(metric) {
	const body = JSON.stringify({
		resourceMetrics: [{
			resource: {
				attributes: [
					{ key: 'service.name', value: { stringValue: 'emper-frontend' } }
				]
			},
			scopeMetrics: [{ metrics: [metric] }]
		}]
	})
	const url = endpoint.replace(/\/+$/, '') + '/v1/metrics'
	const headers = { 'Content-Type': 'application/json' }
	if (apiKey) headers['Authorization'] = 'Bearer ' + apiKey
	try {
		fetch(url, {
			method: 'POST', headers: headers, body: body, keepalive: true
		}).catch(function () { /* swallow */ })
	}
	catch (e) { /* swallow synchronous errors */ }
}

function nowNanos() { return String(Date.now() * 1_000_000) }

function whitelist(p) {
	const out = {}
	for (const k of Object.keys(p)) {
		if (!ALLOWED.has(k)) {
			if (process.env.NODE_ENV !== 'production') {
				console.warn('[telemetry] rejected key:', k)
			}
			continue
		}
		const v = p[k]
		if (v === undefined || v === null) continue
		out[k] = String(v).slice(0, 64)
	}
	return out
}

function countBucket(r) {
	const n = Array.isArray(r) ? r.length : (r ? 1 : 0)
	if (n === 0)    return '0'
	if (n <= 10)    return '1-10'
	if (n <= 100)   return '11-100'
	if (n <= 1000)  return '101-1000'
	return '>1000'
}

function classifyError(e) {
	if (e && typeof e.code === 'number') return 'parse-' + e.code
	if (e && typeof e.name === 'string') return e.name
	return 'unknown'
}

// Spelled out rather than imported: this module never depends on the
// Parse SDK, instrumentParse() is handed it by the caller.
const PARSE_OBJECT_NOT_FOUND      = 101
const PARSE_INVALID_SESSION_TOKEN = 209
const PARSE_DUPLICATE_VALUE       = 137;
const PARSE_VALIDATION_ERROR      = 142;

/**
 * Splits the failures worth waking up for from the ones that are part
 * of normal operation, so the Errors tile counts the former only.
 * Only ever emitted next to an `error_class`, never on a success data
 * point, which keeps the histograms' common case as cheap as it was.
 *
 * A session expiring is a lifecycle event, not a fault: sessions last
 * eight hours, every user meets this daily, and the app answers it by
 * logging out. Counted as an error it buries the real ones; a single
 * forgotten tab once produced ~650 of these in a day.
 *
 * An object that is gone counts as expected only where the caller said
 * so through expectMissing(). Everywhere else a row vanishing under us
 * is a genuine bug and has to stay visible.
 *
 * This runs for query and cloud timings only. It must never be applied
 * to trackLoginFailure(), where Parse reports a wrong password as 101
 * and the refused-logins panel maps that error_class by hand.
 */
function severityFor(e, expectMissing) {
	const code = e && e.code
	if (code === PARSE_INVALID_SESSION_TOKEN) { return 'warn' }
	if (code === PARSE_OBJECT_NOT_FOUND && expectMissing) { return 'warn' }
	return 'error'
}

function mutationSeverityFor(e, expectMissing) {
	const code = e && e.code;
	if (code === PARSE_INVALID_SESSION_TOKEN) { return 'warn'; }
	if (code === PARSE_DUPLICATE_VALUE)       { return 'warn'; }
	if (code === PARSE_VALIDATION_ERROR)      { return 'warn'; }
	// Deleting a row that is already gone, where the caller said it
	// might be. Same reasoning as the query side: unmarked stays an
	// error, so this cannot quietly swallow a real one.
	if (code === PARSE_OBJECT_NOT_FOUND && expectMissing) {
		return 'warn';
	}
	return 'error';
}

export async function withQueryTiming(queryClass, fn, opts) {
	const t0 = performance.now()
	try {
		const r = await fn()
		trackDuration('emper_query_duration_seconds',
			(performance.now() - t0) / 1000, {
				query_class:         queryClass,
				result_count_bucket: countBucket(r)
			})
		return r
	}
	catch (e) {
		trackDuration('emper_query_duration_seconds',
			(performance.now() - t0) / 1000, {
				query_class:  queryClass,
				error_class:  classifyError(e),
				severity:     severityFor(e, opts && opts.expectMissing),
				query_method: opts && opts.method
			})
		throw e
	}
}

export function trackRouteTiming(routeName, durationMs) {
	trackDuration('emper_route_duration_seconds', durationMs / 1000,
		{ route_name: routeName })
}

export async function withCloudTiming(fnName, fn) {
	const t0 = performance.now()
	try {
		const r = await fn()
		trackDuration('emper_cloud_duration_seconds',
			(performance.now() - t0) / 1000,
			{ function_name: fnName })
		return r
	}
	catch (e) {
		trackDuration('emper_cloud_duration_seconds',
			(performance.now() - t0) / 1000, {
				function_name: fnName,
				error_class:   classifyError(e),
				severity:      severityFor(e, false)
			})
		throw e
	}
}

/**
 * Monkey-patch a Parse SDK module so every Query .find/.first/.get/
 * .count and every Parse.Cloud.run emits timing metrics. Idempotent,
 * guarded by a flag on the Parse module itself.
 */
export async function withMutationTiming(entityType, fn, opts) {
	const t0 = performance.now();
	try {
		const r = await fn();
		trackDuration('emper_mutation_duration_seconds',
			(performance.now() - t0) / 1000,
			{ entity_type: entityType });
		return r;
	}
	catch (e) {
		trackDuration('emper_mutation_duration_seconds',
			(performance.now() - t0) / 1000, {
				entity_type: entityType,
				error_class: classifyError(e),
				severity:    mutationSeverityFor(e,
					opts && opts.expectMissing)
			});
		throw e;
	}
}

export function instrumentParse(Parse) {
	if (!Parse || Parse.__emperInstrumented) return
	Parse.__emperInstrumented = true

	const Q = Parse.Query && Parse.Query.prototype
	if (Q) {
		for (const m of [ 'find', 'first', 'get', 'count' ]) {
			const orig = Q[m]
			if (typeof orig !== 'function') continue
			Q[m] = async function () {
				const args = arguments
				return withQueryTiming(this.className || 'unknown',
					() => orig.apply(this, args),
					{ method: m, expectMissing: this.__emperExpectMissing })
			}
		}
	}

	const O = Parse.Object && Parse.Object.prototype;
	if (O) {
		for (const m of [ 'save', 'destroy' ]) {
			const orig = O[m];
			if (typeof orig !== 'function') continue;
			O[m] = function () {
				const args = arguments;
				return withMutationTiming(this.className || 'unknown',
					() => orig.apply(this, args),
					{ expectMissing: this.__emperExpectMissing });
			};
		}
	}

	// The batch statics are separate functions, not the prototype
	// ones, so a saveAll would otherwise leave no trace at all.
	for (const m of [ 'saveAll', 'destroyAll' ]) {
		const orig = Parse.Object && Parse.Object[m];
		if (typeof orig !== 'function') continue;
		Parse.Object[m] = function (objects) {
			const rest = Array.prototype.slice.call(arguments, 1);
			const className = (objects && objects[0]
				&& objects[0].className) || 'unknown';
			const marked = !!(objects && objects[0]
				&& objects[0].__emperExpectMissing);
			return withMutationTiming(className,
				() => orig.apply(Parse.Object, [ objects ].concat(rest)),
				{ expectMissing: marked });
		};
	}

	const Cloud = Parse.Cloud
	if (Cloud && typeof Cloud.run === 'function') {
		const orig = Cloud.run
		Cloud.run = function (name) {
			const args = arguments
			return withCloudTiming(name || 'unknown',
				() => orig.apply(this, args))
		}
	}
}
